import type { Editor } from "ckeditor5"
import { createObjectFitOptions, extractObjectFitFromStyle, hasMarkerFormat, extractObjectFitFromMarker } from "./utils"

export interface ImageDimensions {
  width: string | number | null
  height: string | number | null
  objectFit: string
}

export function getCurrentDimensions(editor: Editor) {
  const selection = editor.model.document.selection
  const selectedElement = selection.getSelectedElement()

  let width = ""
  let height = ""
  let objectFit = ""

  if (!selectedElement || !["image", "imageInline", "imageBlock"].includes(selectedElement.name)) {
    return { width, height, objectFit }
  }

  const widthAttr = selectedElement.getAttribute("width")
  const heightAttr = selectedElement.getAttribute("height")

  if (widthAttr) width = String(widthAttr)
  if (heightAttr) height = String(heightAttr)

  // 1. Check model attribute
  objectFit = String(selectedElement.getAttribute("objectFit") || "")

  // 2. Check parent paragraph marker (from upcast)
  if (!objectFit) {
    const parent = (selectedElement as any).parent
    if (parent && "getAttribute" in parent) {
      const parentAttr = (parent as any).getAttribute("objectFit")
      if (hasMarkerFormat(parentAttr)) {
        objectFit = extractObjectFitFromMarker(parentAttr)
      }
    }
  }

  // 3. Check DOM data-object-fit attribute
  if (!objectFit) {
    const viewElement = editor.editing.mapper.toViewElement(selectedElement)
    if (viewElement) {
      const domElement = editor.editing.view.domConverter.mapViewToDom(viewElement)
      if (domElement && domElement.nodeType === 1) {
        const imgElement = (domElement as Element).querySelector("img") || (domElement as Element)
        objectFit = imgElement.getAttribute("data-object-fit") || ""
      }
    }
  }

  // 4. Check style attribute
  if (!objectFit) {
    const viewElement = editor.editing.mapper.toViewElement(selectedElement)
    if (viewElement) {
      const domElement = editor.editing.view.domConverter.mapViewToDom(viewElement)
      if (domElement && domElement.nodeType === 1) {
        const imgElement = (domElement as Element).querySelector("img") || (domElement as Element)
        const style = imgElement.getAttribute("style") || ""
        objectFit = extractObjectFitFromStyle(style) || ""
      }
    }
  }

  return { width, height, objectFit }
}

export function createDialogHtml(currentDimensions: ImageDimensions): HTMLElement {
  const container = document.createElement("div")
  container.className = "ck-image-dimensions-popup"
  container.innerHTML = `
    <div class="ck-image-dimensions-backdrop"></div>
    <div class="ck-image-dimensions-content">
      <div class="ck-image-dimensions-row">
        <label>Width (px):</label>
        <input type="number" id="img-dim-width" value="${currentDimensions.width}" placeholder="px" min="1" />
      </div>
      <div class="ck-image-dimensions-row">
        <label>Height (px):</label>
        <input type="number" id="img-dim-height" value="${currentDimensions.height}" placeholder="px" min="1" />
      </div>
      <div class="ck-image-dimensions-row">
        <label>Object fit:</label>
        <select id="img-dim-object-fit">${createObjectFitOptions()}</select>
      </div>
      <div class="ck-image-dimensions-buttons">
        <button id="img-dim-apply">Áp dụng</button>
        <button id="img-dim-cancel">Hủy</button>
      </div>
    </div>
  `

  if (currentDimensions.objectFit) {
    const select = container.querySelector("#img-dim-object-fit") as HTMLSelectElement | null
    if (select) {
      select.value = currentDimensions.objectFit
    }
  }

  return container
}

export function getDialogValues(container: HTMLElement): ImageDimensions {
  const widthInput = container.querySelector("#img-dim-width") as HTMLInputElement | null
  const heightInput = container.querySelector("#img-dim-height") as HTMLInputElement | null
  const objectFitSelect = container.querySelector("#img-dim-object-fit") as HTMLSelectElement | null

  return {
    width: widthInput?.value ? parseInt(widthInput.value) : null,
    height: heightInput?.value ? parseInt(heightInput.value) : null,
    objectFit: objectFitSelect?.value || "",
  }
}
